import { useField, useFormikContext } from 'formik';
import { Field, Input, type FieldConfig, type InputProps } from '@kolking/react-ui';

import { useSpec } from './YupSpecContext';

export interface FormInputProps extends FieldConfig, InputProps {
  name: string;
}

export const FormInput = ({
  id,
  name,
  label,
  help,
  className,
  disabled = false,
  readOnly,
  required,
  children,
  ...props
}: FormInputProps) => {
  const { isSubmitting, submitCount } = useFormikContext();
  const [{ value, ...field }, meta] = useField<unknown>(name);
  const spec = useSpec();
  const schema = spec.getSchema(name);
  const error = meta.touched || submitCount ? meta.error : undefined;
  const inputProps = schema?.tests.reduce((memo, { OPTIONS: { name, params } = {} }) => {
    if (params) {
      switch (name) {
        case 'matches':
          if (params.regex instanceof RegExp) {
            memo.pattern = params.regex.toString();
          }
          break;
        case 'length':
          if (typeof params.length === 'number') {
            memo.maxLength = params.length;
          }
          break;
        case 'max':
          if (typeof params.max === 'number') {
            memo.maxLength = params.max;
          }
          break;
      }
    }
    return memo;
  }, {} as Partial<InputProps>);

  return (
    <Field
      className={className}
      required={spec.isRequired(name, required)}
      label={spec.label(name, label)}
      help={help}
      id={id ?? name}
    >
      <Input
        {...inputProps}
        {...props}
        {...field}
        id={id ?? name}
        disabled={spec.isDisabled(name, disabled || isSubmitting)}
        readOnly={spec.isReadOnly(name, readOnly)}
        error={error}
        value={typeof value === 'string' ? value : ''}
      >
        {children}
      </Input>
    </Field>
  );
};
